import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Test } from '../../pages/ScheduleTestManagement';
import {
  useFetchAssociatedStudentsQuery,
  useLazyFetchScheduleTestsQuery,
  useLazyViewScheduleTestStudentDetailsQuery
} from '../../services/scheduleTestApi';
import Button from '../Common/Button';

import AssigneeDialog from './AssigneeDialog';
import TestDetailsDialog from './TestDetailsDialog';

export type TestStatus = 'COMPLETED' | 'STARTED' | 'NOT_YET';

export type AssigneeDetails = {
  name: string;
  emailId: string;
  mobileNumber: string;
  testStatus: TestStatus;
  userId: string;
  testId: string;
  userTestId: string | null;
  startedOn: string | null;
  submittedOn: string | null;
};

const ScheduleTestsTable = ({
  handleAddNewTest,
  tests,
  setTests
}: {
  handleAddNewTest: () => void;
  tests: Test[];
  setTests: any;
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalTests, setTotalTests] = useState(0);
  const [fetchScheduleTests] = useLazyFetchScheduleTestsQuery();
  const [searchValue, setSearchValue] = useState('');
  const [openAssigneesDialog, setOpenAssigneesDialog] = useState(false);
  const [openTestDialog, setOpenTestDialog] = useState(false);
  const [id, setId] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentTestStatus, setCurrentTestStatus] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState<AssigneeDetails[]>(
    []
  );
  const [selectedTest, setSelectedTest] = useState<Test>({} as Test);
  const { data: associatedStudents } = useFetchAssociatedStudentsQuery({});
  const [viewScheduleTestStudentDetails] =
    useLazyViewScheduleTestStudentDetailsQuery();

  const handleClickRefresh = async () => {
    setIsRefreshing(true);
    if (id) {
      try {
        const res = await viewScheduleTestStudentDetails(id).unwrap();
        setSelectedAssignees(res?.data || []);
        setIsRefreshing(false);
      } catch (err: any) {
        setIsRefreshing(false);

        toast.error(err?.data?.message);
        console.log(err);
      }
    }
  };

  const handleOpenAssignees = async (test: Test) => {
    setId(test?.id);
    setCurrentTestStatus(test?.testStatus);
    try {
      const res = await viewScheduleTestStudentDetails(test?.id).unwrap();
      console.log(res, 'res1234');
      setSelectedAssignees(res?.data || []);
      setOpenAssigneesDialog(true);
    } catch (err: any) {
      toast.error(err?.data?.message);
      console.log(err);
    }
  };

  const handleOpenTestDetails = (test: Test) => {
    setSelectedTest(test);
    setOpenTestDialog(true);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(0);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil((totalTests || 0) / pageSize);
    const currentPageGroup = Math.floor(currentPage / 10);
    const pages = Array.from(
      { length: Math.min(10, totalPages - currentPageGroup * 10) },
      (_, i) => currentPageGroup * 10 + i
    );
    const handlePageChange = (newPage: number) => {
      setCurrentPage(newPage);
    };

    return (
      <div className='mt-6 flex flex-wrap items-center justify-between gap-4 rounded-b-xl border-t border-gray-200 px-6 py-4'>
        <div className='flex items-center gap-2'>
          <span className='text-sm text-gray-600'>Show</span>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className='cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-all hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20'
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className='text-sm text-gray-600'>entries</span>
        </div>

        <div className='flex items-center gap-3'>
          <Button
            label={<ChevronLeft size={18} />}
            variant='none'
            classes='h-9 w-9 rounded-lg border border-gray-300 hover:border-primary hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400'
            handleClickBtn={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          />

          <div className='flex items-center gap-1'>
            {pages.map(page => (
              <Button
                key={page}
                label={page + 1}
                variant='none'
                classes={`h-9 min-w-[36px] px-3 rounded-lg font-medium text-sm transition-all ${
                  currentPage === page
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                handleClickBtn={() => handlePageChange(page)}
              />
            ))}
          </div>

          <Button
            label={<ChevronRight size={18} />}
            variant='none'
            classes='h-9 w-9 rounded-lg border border-gray-300 hover:border-primary hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400'
            handleClickBtn={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          />
        </div>

        <div className='flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-2'>
          <span className='text-sm text-gray-600'>Total Tests:</span>
          <span className='text-sm font-semibold text-primary'>
            {totalTests}
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, totalScheduledTestsCount } = await fetchScheduleTests({
          page: currentPage,
          size: pageSize
        }).unwrap();

        setTests(data);
        setTotalTests(totalScheduledTestsCount);
      } catch (error) {
        console.error('Failed to fetch test results:', error);
      }
    };

    fetchData();
  }, [currentPage, pageSize, fetchScheduleTests, setTests]);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value.toLowerCase());
  };

  const filteredTests = tests.filter(test =>
    test?.testName?.toLowerCase().includes(searchValue)
  );

  const getStatusColors = (status: string) => {
    switch (status) {
      case 'UPCOMING':
        return 'text-primary';
      case 'ENDED':
        return 'text-red-600';
      default:
        return 'text-green-600';
    }
  };

  return (
    <div className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
      <div className='p-6'>
        <h2 className='mb-6 text-2xl font-semibold text-gray-900'>
          Scheduled Tests
        </h2>

        <div className='mb-6 flex gap-4'>
          <div className='relative flex-1'>
            <Search className='absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400' />
            <input
              onChange={handleChangeSearch}
              type='text'
              placeholder='Search by test'
              className='w-full rounded-xl border border-gray-300 py-3 pl-12 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>
          <button
            onClick={handleAddNewTest}
            className='flex flex-shrink-0 items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700'
          >
            <Plus className='h-5 w-5' />
            Add new test
          </button>
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead>
              <tr className='border-b border-gray-200'>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Test Name
                </th>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Status
                </th>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Created Date
                </th>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Test Start Date
                </th>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Test End Date
                </th>
                <th className='px-4 py-4 text-left text-sm font-medium text-blue-600'>
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTests?.map(test => (
                <tr key={test?.id} className='border-b border-gray-100'>
                  <td className='max-w-[280px] px-4 py-4 text-sm text-gray-900'>
                    <span className='block truncate' title={test?.testName}>
                      {test?.testName}
                    </span>
                  </td>
                  <td className='px-4 py-4'>
                    <button
                      className={`${getStatusColors(test?.testStatus)} flex items-center gap-2 rounded-full py-1 text-sm font-medium transition-colors`}
                    >
                      {test?.testStatus}
                    </button>
                  </td>
                  <td className='px-4 py-4 text-sm text-gray-900'>
                    {new Date(test?.createdDate)?.toISOString()?.slice(0, 10)}
                  </td>
                  <td className='px-4 py-4 text-sm text-gray-900'>
                    {new Date(`${test?.testStartTime}z`)?.toLocaleString()}
                  </td>
                  <td className='px-4 py-4 text-sm text-gray-900'>
                    {new Date(`${test?.testEndTime}z`)?.toLocaleString()}
                  </td>
                  <td className='px-4 py-4 text-sm text-gray-900 '>
                    <Button
                      label='View Details'
                      handleClickBtn={() => handleOpenAssignees(test)}
                      variant='contained'
                      classes='!text-sm !px-2 !py-2 !w-[120px] !ml-0 !hover:bg-secondary'
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination */}
      {renderPagination()}
      <AssigneeDialog
        setOpenAssigneesDialog={setOpenAssigneesDialog}
        openAssigneesDialog={openAssigneesDialog}
        selectedAssignees={selectedAssignees}
        handleClickRefresh={handleClickRefresh}
        isRefreshing={isRefreshing}
        testStatus={currentTestStatus}
      />
      <TestDetailsDialog
        setOpenTestDialog={setOpenTestDialog}
        selectedTest={selectedTest}
        openTestDialog={openTestDialog}
      />
    </div>
  );
};

export default ScheduleTestsTable;
